import type { CSSProperties } from 'react'
import type { ForecastState } from '../../types'
import type { ForecastPoint } from '@nextweight/shared'
import { useTranslation } from '../../lib/i18n'

interface ForecastListProps {
  state: ForecastState
}

const HORIZON_KEYS: Record<number, string> = {
  30:  'horizon.1_month',
  90:  'horizon.3_months',
  180: 'horizon.6_months',
  365: 'horizon.1_year',
}

const HORIZON_SHORT_KEYS: Record<number, string> = {
  30:  'horizon.short.1_month',
  90:  'horizon.short.3_months',
  180: 'horizon.short.6_months',
  365: 'horizon.short.1_year',
}

function fmtDelta(delta: number): string {
  const abs = Math.abs(delta).toFixed(1)
  return delta >= 0 ? `+${abs}` : `−${abs}`
}

interface ForecastRowProps {
  point: ForecastPoint
  currentSmoothed: number
  isLow: boolean
}

function ForecastRow({ point, currentSmoothed, isLow }: ForecastRowProps) {
  const { t } = useTranslation()
  const label = HORIZON_KEYS[point.days] ? t(HORIZON_KEYS[point.days]!) : `${point.days}${t('unit.days_short')}`

  const deltaCenter = point.value - currentSmoothed
  const deltaMin = point.min - currentSmoothed
  const deltaMax = point.max - currentSmoothed

  return (
    <div style={{ ...rowStyles.root, opacity: isLow ? 0.3 : 1 }}>
      <span style={rowStyles.label}>{label}</span>
      <span style={rowStyles.center}>{fmtDelta(deltaCenter)} {t('unit.kg')}</span>
      <span style={rowStyles.range}>
        ({fmtDelta(deltaMin)} {t('unit.range_to')} {fmtDelta(deltaMax)})
      </span>
    </div>
  )
}

function buildSummary(
  state: ForecastState,
  t: (key: string, params?: Record<string, string>) => string,
): string | null {
  if (state.forecast.length === 0) return null
  const last = state.forecast[state.forecast.length - 1]!
  const delta = last.value - state.current.weight_smoothed
  if (Math.abs(delta) < 0.1) return null

  const horizonKey = HORIZON_SHORT_KEYS[last.days]
  const horizon = horizonKey ? t(horizonKey) : `${last.days}${t('unit.days_short')}`
  const key = delta < 0 ? 'forecast.summary_loss' : 'forecast.summary_gain'
  return t(key, { delta: Math.abs(delta).toFixed(1), horizon })
}

export function ForecastList({ state }: ForecastListProps) {
  const { t } = useTranslation()

  if (state.status === 'WARMUP' || state.status === 'PLATEAU') return null
  if (state.forecast.length === 0) return null

  const isLow = state.current.accuracy === 'LOW'
  const summary = buildSummary(state, t)

  return (
    <div style={styles.root}>
      <div style={styles.label}>{t('forecast.title')}</div>
      {summary && <div style={styles.summary}>{summary}</div>}
      {state.forecast.map((point) => (
        <ForecastRow
          key={point.days}
          point={point}
          currentSmoothed={state.current.weight_smoothed}
          isLow={isLow}
        />
      ))}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  root: { padding: '12px 0' },
  label: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.8,
    color: 'var(--tg-theme-hint-color, #888)',
    marginBottom: 8,
  },
  summary: {
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--tg-theme-text-color, #000)',
    marginBottom: 12,
  },
}

const rowStyles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '8px 0',
    borderBottom: '1px solid var(--tg-theme-secondary-bg-color, #f3f4f6)',
  },
  label: {
    flex: 1,
    fontSize: 15,
    color: 'var(--tg-theme-hint-color, #888)',
  },
  center: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  range: {
    fontSize: 13,
    color: 'var(--tg-theme-hint-color, #888)',
  },
}
